import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Alert,
  FormControlLabel,
  Switch,
  MenuItem
} from '@mui/material';
import {
  Activity,
  Plus,
  Edit2,
  Trash2,
  Calendar,
  User,
  FileText,
  CheckCircle,
  X
} from 'lucide-react';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { conditionsCatalogService, userConditionService } from '../services/agthealthConditionService';
import AppleSpinner from '../../common/AppleSpinner';
import { format, parseISO, addDays } from 'date-fns';
import { es } from 'date-fns/locale';

const STATUS_LABELS = {
  active: 'Activa',
  in_remission: 'En remisión',
  recovered: 'Recuperada'
};

const TYPE_LABELS = {
  chronic: 'Crónica',
  acute: 'Aguda'
};

// Constantes de color para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Componentes estilizados
const StyledCard = styled(Card)(({ theme, wallpaperMode }) => ({
  borderRadius: '16px',
  boxShadow: wallpaperMode
    ? 'none'
    : '0 2px 12px rgba(0, 0, 0, 0.08)',
  border: wallpaperMode
    ? `1px solid ${WALLPAPER_COLORS.border}`
    : '1px solid rgba(0, 0, 0, 0.08)',
  backgroundColor: wallpaperMode
    ? WALLPAPER_COLORS.bg
    : '#FFFFFF',
  backdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: wallpaperMode
      ? `0 8px 24px ${WALLPAPER_COLORS.border}`
      : '0 8px 24px rgba(0, 0, 0, 0.12)',
  }
}));

const ActionButton = styled(Button)(({ theme, wallpaperMode }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '10px 24px',
  boxShadow: 'none',
  backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bg : theme.palette.primary.main,
  color: wallpaperMode ? WALLPAPER_COLORS.text : '#FFFFFF',
  border: wallpaperMode ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  backdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  WebkitBackdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
  '&:hover': {
    boxShadow: wallpaperMode
      ? `0 4px 12px ${WALLPAPER_COLORS.border}`
      : '0 4px 12px rgba(0, 0, 0, 0.15)',
    backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : theme.palette.primary.dark,
  }
}));

const Conditions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const wallpaperMode = theme.palette.actualMode === 'wallpaper';

  const [conditions, setConditions] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCondition, setEditingCondition] = useState(null);
  const [useCatalog, setUseCatalog] = useState(true);
  const [formData, setFormData] = useState({
    conditionId: '',
    name: '',
    type: 'chronic',
    category: '',
    status: 'active',
    severity: 'moderate',
    diagnosisDate: format(new Date(), 'yyyy-MM-dd'),
    estimatedDuration: '',
    diagnosedBy: '',
    notes: '',
    treatment: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const catalogOptions = useMemo(() => {
    return [...catalog].sort((a, b) => {
      const nameA = (a.nameEs || a.name || '').toLowerCase();
      const nameB = (b.nameEs || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [catalog]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [conditionsResponse, catalogResponse] = await Promise.all([
        userConditionService.getAll(),
        conditionsCatalogService.getAll()
      ]);

      if (conditionsResponse.success) {
        setConditions(conditionsResponse.data);
      }

      if (catalogResponse.success) {
        setCatalog(catalogResponse.data);
      }
    } catch (loadError) {
      console.error('Error al cargar condiciones:', loadError);
      setError('Error al cargar las condiciones');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (condition = null) => {
    if (condition) {
      const diagnosisDate = condition.diagnosisDate
        ? format(parseISO(condition.diagnosisDate), 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd');

      setEditingCondition(condition);
      setUseCatalog(!condition.isCustom);
      setFormData({
        conditionId: condition.conditionId || '',
        name: condition.nameEs || condition.name || '',
        type: condition.type || 'chronic',
        category: condition.category || '',
        status: condition.status || 'active',
        severity: condition.severity || 'moderate',
        diagnosisDate,
        estimatedDuration: condition.estimatedDuration || '',
        diagnosedBy: condition.diagnosedBy || '',
        notes: condition.notes || '',
        treatment: condition.treatment || ''
      });
    } else {
      setEditingCondition(null);
      setUseCatalog(true);
      setFormData({
        conditionId: '',
        name: '',
        type: 'chronic',
        category: '',
        status: 'active',
        severity: 'moderate',
        diagnosisDate: format(new Date(), 'yyyy-MM-dd'),
        estimatedDuration: '',
        diagnosedBy: '',
        notes: '',
        treatment: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCondition(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCatalogToggle = (event) => {
    if (editingCondition) {
      return;
    }
    const enabled = event.target.checked;
    setUseCatalog(enabled);
    setFormData(prev => ({
      ...prev,
      conditionId: '',
      name: '',
      category: '',
      type: 'chronic',
      estimatedDuration: ''
    }));
  };

  const handleCatalogSelection = (event) => {
    const selectedId = event.target.value;
    const selectedCondition = catalogOptions.find(item => item.conditionId === selectedId);

    setFormData(prev => ({
      ...prev,
      conditionId: selectedId,
      name: selectedCondition?.nameEs || selectedCondition?.name || '',
      type: selectedCondition?.type || 'chronic',
      category: selectedCondition?.category || '',
      estimatedDuration: selectedCondition?.defaultDuration || ''
    }));
  };

  const handleSubmit = async () => {
    try {
      setError('');
      setLoading(true);

      if (editingCondition) {
        const updateData = {
          status: formData.status,
          severity: formData.severity,
          notes: formData.notes,
          treatment: formData.treatment,
          diagnosedBy: formData.diagnosedBy
        };

        if (editingCondition.type === 'acute' && formData.estimatedDuration) {
          const duration = Number(formData.estimatedDuration);
          if (Number.isNaN(duration) || duration <= 0) {
            setError('La duración estimada debe ser un número válido');
            setLoading(false);
            return;
          }

          updateData.estimatedDuration = duration;
          if (editingCondition.diagnosisDate) {
            updateData.estimatedRecoveryDate = addDays(parseISO(editingCondition.diagnosisDate), duration).toISOString();
          }
        }

        const response = await userConditionService.update(editingCondition.userConditionId, updateData);
        if (response.success) {
          setSuccess('Condición actualizada');
          setTimeout(() => setSuccess(''), 3000);
          handleCloseDialog();
          loadData();
        } else {
          setError(response.error || 'Error al actualizar la condición');
        }
        return;
      }

      if (useCatalog && !formData.conditionId) {
        setError('Selecciona una condición del catálogo');
        setLoading(false);
        return;
      }

      if (!useCatalog && (!formData.name || !formData.type || !formData.category)) {
        setError('Completa nombre, tipo y categoría para condiciones personalizadas');
        setLoading(false);
        return;
      }

      const payload = {
        diagnosisDate: formData.diagnosisDate,
        status: formData.status,
        severity: formData.severity,
        diagnosedBy: formData.diagnosedBy,
        notes: formData.notes,
        treatment: formData.treatment
      };

      if (useCatalog) {
        payload.conditionId = formData.conditionId;
        if (formData.estimatedDuration) {
          payload.estimatedDuration = Number(formData.estimatedDuration);
        }
      } else {
        payload.name = formData.name;
        payload.type = formData.type;
        payload.category = formData.category;
        if (formData.estimatedDuration) {
          payload.estimatedDuration = Number(formData.estimatedDuration);
        }
      }

      const response = await userConditionService.create(payload);
      if (response.success) {
        setSuccess('Condición agregada');
        setTimeout(() => setSuccess(''), 3000);
        handleCloseDialog();
        loadData();
      } else {
        setError(response.error || 'Error al guardar la condición');
      }
    } catch (submitError) {
      console.error('Error al guardar condición:', submitError);
      setError('Error al guardar la condición');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta condición?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await userConditionService.delete(id);
      if (response.success) {
        setSuccess('Condición eliminada');
        setTimeout(() => setSuccess(''), 3000);
        loadData();
      } else {
        setError(response.error || 'Error al eliminar la condición');
      }
    } catch (deleteError) {
      console.error('Error al eliminar condición:', deleteError);
      setError('Error al eliminar la condición');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRecovered = async (condition) => {
    try {
      setLoading(true);
      const response = await userConditionService.markAsRecovered(condition.userConditionId);
      if (response.success) {
        setSuccess('Condición marcada como recuperada');
        setTimeout(() => setSuccess(''), 3000);
        loadData();
      } else {
        setError(response.error || 'Error al actualizar la condición');
      }
    } catch (updateError) {
      console.error('Error al marcar como recuperada:', updateError);
      setError('Error al actualizar la condición');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRemission = async (condition) => {
    try {
      setLoading(true);
      const response = await userConditionService.markAsInRemission(condition.userConditionId);
      if (response.success) {
        setSuccess('Condición marcada en remisión');
        setTimeout(() => setSuccess(''), 3000);
        loadData();
      } else {
        setError(response.error || 'Error al actualizar la condición');
      }
    } catch (updateError) {
      console.error('Error al marcar en remisión:', updateError);
      setError('Error al actualizar la condición');
    } finally {
      setLoading(false);
    }
  };

  const renderStatusChip = (status) => {
    const label = STATUS_LABELS[status] || status;
    let color = 'default';
    if (status === 'active') color = 'warning';
    if (status === 'in_remission') color = 'info';
    if (status === 'recovered') color = 'success';

    return (
      <Chip
        label={label}
        size="small"
        color={color}
        sx={{
          fontWeight: 500,
          backgroundColor: wallpaperMode && status === 'recovered'
            ? 'rgba(76, 175, 80, 0.3)'
            : undefined,
          color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
        }}
      />
    );
  };

  if (loading && conditions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <AppleSpinner sizeDesktop={48} sizeMobile={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Activity size={isMobile ? 24 : 28} color={wallpaperMode ? WALLPAPER_COLORS.text : theme.palette.primary.main} />
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{
              fontWeight: 700,
              color: wallpaperMode ? WALLPAPER_COLORS.text : 'inherit'
            }}
          >
            Condiciones Médicas
          </Typography>
        </Box>
        <ActionButton
          wallpaperMode={wallpaperMode}
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
        >
          {isMobile ? 'Agregar' : 'Agregar Condición'}
        </ActionButton>
      </Box>

      {/* Alertas */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lista de condiciones */}
      <Grid container spacing={2}>
        <AnimatePresence>
          {conditions.map((condition) => {
            const name = condition.nameEs || condition.name;
            const isChronic = condition.type === 'chronic';
            const showRemissionAction = isChronic && condition.status === 'active';
            const showRecoveredAction = condition.status !== 'recovered' && (!isChronic || condition.status === 'in_remission');

            return (
              <Grid item xs={12} sm={6} md={4} key={condition.userConditionId}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  <StyledCard wallpaperMode={wallpaperMode}>
                    <CardContent>
                      {/* Nombre y estado */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1.5 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: wallpaperMode ? WALLPAPER_COLORS.text : 'inherit'
                          }}
                        >
                          {name}
                        </Typography>
                        {renderStatusChip(condition.status)}
                      </Box>

                      {/* Badges */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip
                          label={TYPE_LABELS[condition.type] || condition.type}
                          size="small"
                          color={condition.type === 'chronic' ? 'primary' : 'secondary'}
                          sx={{
                            fontWeight: 500,
                            color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
                          }}
                        />
                        {condition.category && (
                          <Chip
                            label={condition.category}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
                              borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined
                            }}
                          />
                        )}
                        {condition.isCustom && (
                          <Chip
                            label="Personalizada"
                            size="small"
                            variant="outlined"
                            sx={{
                              color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
                              borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined
                            }}
                          />
                        )}
                      </Box>

                      {/* Detalles */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                        {condition.diagnosisDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Calendar size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{ color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary' }}
                            >
                              Diagnóstico: {format(parseISO(condition.diagnosisDate), 'dd/MM/yyyy', { locale: es })}
                            </Typography>
                          </Box>
                        )}
                        {condition.estimatedRecoveryDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Calendar size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{ color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary' }}
                            >
                              Recuperación estimada: {format(parseISO(condition.estimatedRecoveryDate), 'dd/MM/yyyy', { locale: es })}
                            </Typography>
                          </Box>
                        )}
                        {condition.actualRecoveryDate && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Calendar size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{ color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary' }}
                            >
                              Recuperada: {format(parseISO(condition.actualRecoveryDate), 'dd/MM/yyyy', { locale: es })}
                            </Typography>
                          </Box>
                        )}
                        {condition.diagnosedBy && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <User size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{ color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary' }}
                            >
                              {condition.diagnosedBy}
                            </Typography>
                          </Box>
                        )}
                        {condition.treatment && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileText size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Tratamiento: {condition.treatment}
                            </Typography>
                          </Box>
                        )}
                        {condition.notes && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FileText size={14} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
                            <Typography
                              variant="caption"
                              sx={{
                                color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {condition.notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      {/* Acciones */}
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {showRemissionAction && (
                          <Button
                            size="small"
                            startIcon={<CheckCircle size={16} />}
                            onClick={() => handleMarkRemission(condition)}
                            sx={{
                              borderRadius: '12px',
                              textTransform: 'none',
                              color: wallpaperMode ? WALLPAPER_COLORS.text : 'primary.main'
                            }}
                          >
                            En remisión
                          </Button>
                        )}
                        {showRecoveredAction && (
                          <Button
                            size="small"
                            startIcon={<CheckCircle size={16} />}
                            onClick={() => handleMarkRecovered(condition)}
                            sx={{
                              borderRadius: '12px',
                              textTransform: 'none',
                              color: wallpaperMode ? WALLPAPER_COLORS.text : 'success.main'
                            }}
                          >
                            Recuperada
                          </Button>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(condition)}
                          sx={{
                            color: wallpaperMode ? WALLPAPER_COLORS.text : 'primary.main',
                            '&:hover': { backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : undefined }
                          }}
                        >
                          <Edit2 size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(condition.userConditionId)}
                          sx={{
                            color: wallpaperMode ? WALLPAPER_COLORS.text : 'error.main',
                            '&:hover': { backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bgLight : undefined }
                          }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </StyledCard>
                </motion.div>
              </Grid>
            );
          })}
        </AnimatePresence>
      </Grid>

      {/* Mensaje cuando no hay condiciones */}
      {conditions.length === 0 && !loading && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '300px',
            gap: 2
          }}
        >
          <Activity size={48} color={wallpaperMode ? WALLPAPER_COLORS.textSecondary : theme.palette.text.secondary} />
          <Typography
            variant="h6"
            sx={{
              color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary',
              textAlign: 'center'
            }}
          >
            No tienes condiciones registradas
          </Typography>
          <ActionButton wallpaperMode={wallpaperMode} startIcon={<Plus size={20} />} onClick={() => handleOpenDialog()}>
            Agregar tu primera condición
          </ActionButton>
        </Box>
      )}

      {/* Dialog para crear/editar */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            backgroundColor: wallpaperMode ? WALLPAPER_COLORS.bg : undefined,
            backdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
            WebkitBackdropFilter: wallpaperMode ? 'blur(20px)' : 'none',
            border: wallpaperMode ? `1px solid ${WALLPAPER_COLORS.border}` : undefined,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {editingCondition ? 'Editar Condición' : 'Nueva Condición'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <X size={20} color={wallpaperMode ? WALLPAPER_COLORS.text : undefined} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={useCatalog}
                  onChange={handleCatalogToggle}
                  disabled={Boolean(editingCondition)}
                />
              }
              label={useCatalog ? 'Seleccionar del catálogo' : 'Condición personalizada'}
              sx={{
                color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : 'text.secondary'
              }}
            />

            {useCatalog ? (
              <TextField
                select
                label="Condición del catálogo *"
                name="conditionId"
                value={formData.conditionId}
                onChange={handleCatalogSelection}
                fullWidth
                disabled={Boolean(editingCondition)}
                InputLabelProps={{
                  sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                    '& fieldset': {
                      borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                    },
                  }
                }}
              >
                {catalogOptions.map((option) => (
                  <MenuItem key={option.conditionId} value={option.conditionId}>
                    {option.nameEs || option.name} · {TYPE_LABELS[option.type] || option.type}
                  </MenuItem>
                ))}
              </TextField>
            ) : (
              <>
                <TextField
                  label="Nombre de la condición *"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  fullWidth
                  disabled={Boolean(editingCondition)}
                  InputLabelProps={{
                    sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                      '& fieldset': {
                        borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                      },
                    }
                  }}
                />
                <TextField
                  select
                  label="Tipo *"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  fullWidth
                  disabled={Boolean(editingCondition)}
                  InputLabelProps={{
                    sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                      '& fieldset': {
                        borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                      },
                    }
                  }}
                >
                  <MenuItem value="chronic">Crónica</MenuItem>
                  <MenuItem value="acute">Aguda</MenuItem>
                </TextField>
                <TextField
                  label="Categoría *"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  fullWidth
                  disabled={Boolean(editingCondition)}
                  placeholder="ej: respiratoria, digestiva"
                  InputLabelProps={{
                    sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                      '& fieldset': {
                        borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                      },
                    }
                  }}
                />
              </>
            )}

            <TextField
              select
              label="Estado"
              name="status"
              value={formData.status}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            >
              <MenuItem value="active">Activa</MenuItem>
              <MenuItem value="in_remission">En remisión</MenuItem>
              <MenuItem value="recovered">Recuperada</MenuItem>
            </TextField>

            <TextField
              select
              label="Severidad"
              name="severity"
              value={formData.severity}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            >
              <MenuItem value="mild">Leve</MenuItem>
              <MenuItem value="moderate">Moderada</MenuItem>
              <MenuItem value="severe">Severa</MenuItem>
            </TextField>

            <TextField
              label="Fecha de diagnóstico"
              name="diagnosisDate"
              type="date"
              value={formData.diagnosisDate}
              onChange={handleChange}
              fullWidth
              InputLabelProps={{
                shrink: true,
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />

            {(formData.type === 'acute') && (
              <TextField
                label="Duración estimada (días)"
                name="estimatedDuration"
                type="number"
                value={formData.estimatedDuration}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{
                  sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                    '& fieldset': {
                      borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                    },
                  },
                }}
              />
            )}

            <TextField
              label="Diagnosticado por"
              name="diagnosedBy"
              value={formData.diagnosedBy}
              onChange={handleChange}
              fullWidth
              placeholder="ej: Dr. Gómez"
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />

            <TextField
              label="Tratamiento"
              name="treatment"
              value={formData.treatment}
              onChange={handleChange}
              fullWidth
              placeholder="Tratamiento actual..."
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />

            <TextField
              label="Notas"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Notas adicionales..."
              InputLabelProps={{
                sx: { color: wallpaperMode ? WALLPAPER_COLORS.textSecondary : undefined }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: wallpaperMode ? WALLPAPER_COLORS.text : undefined,
                  '& fieldset': {
                    borderColor: wallpaperMode ? WALLPAPER_COLORS.border : undefined,
                  },
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
              color: wallpaperMode ? WALLPAPER_COLORS.text : undefined
            }}
          >
            Cancelar
          </Button>
          <ActionButton wallpaperMode={wallpaperMode} onClick={handleSubmit} disabled={loading}>
            {loading ? <AppleSpinner sizeDesktop={20} sizeMobile={16} /> : (editingCondition ? 'Actualizar' : 'Agregar')}
          </ActionButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Conditions;
